import { Loader2Icon, WandSparklesIcon } from "lucide-react";

import { Column, Text } from "@/components";
import { Button } from "@/components/ui/button";

import type { EnrichmentCandidacy } from "./types";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useCreateAddCandidacyArtifacts } from "@/hooks/createAdds/useCreateAddCandidacyArtifacts";
import { useApplyEnrichment } from "@/hooks/mutations/useApplyEnrichment";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { EnrichedProfileState } from "@/types";

// Saves the accumulated enrichment back to the candidate's profile.
export const OverwriteProfileButton = ({
  candidacy,
  enrichedProfile,
  isEnriching,
  onApplied,
}: {
  candidacy: EnrichmentCandidacy;
  enrichedProfile: EnrichedProfileState;
  isEnriching: boolean;
  onApplied: () => void;
}) => {
  const apply = useApplyEnrichment();
  const artifacts = useCreateAddCandidacyArtifacts();
  const successToast = useSuccessToast();
  const errorToast = useErrorToast();

  const userId = candidacy.talentId;
  const isWorking = apply.isPending || artifacts.isPending;
  const isDisabled = isEnriching || isWorking || userId === null;

  const handleClick = () => {
    if (userId === null) return;

    mixpanelUtils.trackButtonClick(
      "Overwrite Profile",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      { candidacy_id: candidacy.candidacyId },
    );

    // useApplyEnrichment writes the summary and refreshed profile into the
    // caches at the hook level.
    apply.mutate(
      {
        candidacyId: candidacy.candidacyId,
        enrichedProfile,
        userId,
      },
      {
        onSuccess: () => {
          // Regenerate the resume + stored exec summary packet from the
          // settled profile. The portal stays open with a "Creating resume"
          // state until the artifacts land. Active users only.
          artifacts.mutate(
            {
              candidacyId: candidacy.candidacyId,
              talentId: candidacy.talentId,
            },
            {
              onSuccess: () => {
                successToast({
                  description: "Profile updated and resume created.",
                });
                onApplied();
              },
              onError: () => {
                errorToast({
                  description: "Profile updated, but resume creation failed.",
                });
                onApplied();
              },
            },
          );
        },
        onError: (error) => {
          errorToast({
            description: error.message || "Failed to overwrite profile.",
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Column className="gap-2 px-1">
        <Text className="text-xs" variant="muted">
          If this looks good,{" "}
          <span className="font-medium text-slate-700">overwrite profile</span>.
          Otherwise, continue editing.
        </Text>
        <Button
          className="w-fit gap-2 rounded-full"
          disabled={isDisabled}
          onClick={handleClick}
          size="sm"
          type="button"
        >
          {isWorking ? (
            <Loader2Icon className="h-4 w-4 animate-spin" />
          ) : (
            <WandSparklesIcon className="h-4 w-4" />
          )}
          {isWorking ? "Creating resume..." : "Overwrite profile"}
        </Button>
      </Column>
    );
  };

  return render();
};
